import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Button,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout";
import {
  RevenueForecastChart,
  SiteCostTrendChart,
  BillDistributionChart,
  CorrelationScatterChart,
  BalanceAgingChart,
  TimeOfUseDonutChart,
  StatCard,
} from "./components/ReportsV2Charts";
import { useNavigate } from "react-router-dom";

const BillingAnalyticsV2: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(3);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  // Filter states
  const [forecastDays, setForecastDays] = useState(14);

  const [data, setData] = useState({
    revenueForecast: { actual: [] as any[], forecast: [] as any[] },
    siteCostTrend: [] as any[],
    billDistribution: [] as any[],
    balanceAging: [
      { bucket: "0-30 Days", amount: 4500000 },
      { bucket: "31-60 Days", amount: 2800000 },
      { bucket: "61-90 Days", amount: 1500000 },
      { bucket: ">90 Days", amount: 900000 },
    ],
    touDistribution: [
      { name: "Off-Peak", value: 45 },
      { name: "Mid-Peak", value: 25 },
      { name: "Peak", value: 20 },
      { name: "Crit-Peak", value: 10 },
    ],
  });

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/reports-v2/overview");
    if (newValue === 1) navigate("/reports-v2/consumption");
    if (newValue === 2) navigate("/reports-v2/revenue-billing");
    if (newValue === 3) navigate("/reports-v2/billing-analytics");
  };

  const fetchReportData = async () => {
    setFetching(true);
    try {
      const [forecastRes, siteTrendRes, distRes] = await Promise.all([
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/revenue-forecast?forecastDays=${forecastDays}`,
        ),
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/site-cost-trend?days=30`,
        ),
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/bill-distribution`,
        ),
      ]);

      const forecast = forecastRes.data.success
        ? forecastRes.data.data
        : forecastRes.data;
      const siteTrend = Array.isArray(siteTrendRes.data)
        ? siteTrendRes.data
        : siteTrendRes.data.data || [];
      const dist = Array.isArray(distRes.data)
        ? distRes.data
        : distRes.data.data || [];

      console.log("Analytics Data Cleaned:", { forecast, siteTrend, dist });

      setData((prev) => ({
        ...prev,
        revenueForecast: forecast || { actual: [], forecast: [] },
        siteCostTrend: siteTrend,
        billDistribution: dist,
      }));
    } catch (error) {
      console.error("Error fetching billing analytics data:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchReportData().then(() => setLoading(false));
  }, []);

  // Calculate Metrics
  const forecastArray = Array.isArray(data.revenueForecast.forecast)
    ? data.revenueForecast.forecast
    : [];
  const actualArray = Array.isArray(data.revenueForecast.actual)
    ? data.revenueForecast.actual
    : [];
  const distArray = Array.isArray(data.billDistribution)
    ? data.billDistribution
    : [];
  const agingArray = Array.isArray(data.balanceAging) ? data.balanceAging : [];

  const forecastTotal = forecastArray.reduce(
    (acc: number, curr: any) => acc + (curr.forecast || 0),
    0,
  );
  const actualLastValue =
    actualArray.length > 0 ? actualArray[actualArray.length - 1].actual : 0;
  const forecastFirstValue =
    forecastArray.length > 0 ? forecastArray[0].forecast : 0;
  const dailyGrowth =
    actualLastValue > 0
      ? ((forecastFirstValue - actualLastValue) / actualLastValue) * 100
      : 0;

  const totalBillAmount = distArray.reduce(
    (acc: number, curr: number) => acc + curr,
    0,
  );
  const avgBill = distArray.length > 0 ? totalBillAmount / distArray.length : 0;
  const outstandingBal = agingArray.reduce(
    (acc: number, curr: any) => acc + curr.amount,
    0,
  );

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 0, minHeight: "100vh", color: "#181B25" }}>
        <Box sx={{ mb: 3, pt: 0, px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                minHeight: "48px",
              },
              "& .Mui-selected": { color: "#1064ED !important" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1064ED",
                height: "3px",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Consumption" />
            <Tab label="Revenue & Billing" />
            <Tab label="Billing Analytics" />
            <Tab label="Power Quality" />
            <Tab label="Predictive Analytics" />
          </Tabs>
        </Box>

        <Box sx={{ px: 1, pb: 4 }}>
          {/* Metric Cards */}
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="REVENUE FORECAST"
                value={`$${(forecastTotal / 1000).toFixed(1)}K`}
                subValue="7D PROJECTION"
                color="#1064ED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="DAILY GROWTH"
                value={`${dailyGrowth.toFixed(1)}%`}
                subValue="VS PREV PERIOD"
                color="#10B981"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="AVG BILL AMOUNT"
                value={`$${avgBill.toFixed(0)}`}
                subValue="SYSTEM WIDE"
                color="#7C3AED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="OUTSTANDING BAL"
                value={`$${(outstandingBal / 1000000).toFixed(1)}M`}
                subValue="TOTAL ARREARS"
                color="#EF4444"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="COLLECTION RISK"
                value="Medium"
                subValue="LEVEL"
                color="#F59E0B"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="PROBABLE ARREARS"
                value={`$${((outstandingBal * 0.15) / 1000).toFixed(0)}K`}
                subValue="NEXT 30D"
                color="#EC4899"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Revenue Forecast Chart */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "rgba(0,0,0,0.7)",
                        fontWeight: 700,
                        fontSize: "16px",
                      }}
                    >
                      Revenue Forecast (Prophet Model)
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        select
                        size="small"
                        value={forecastDays}
                        onChange={(e) =>
                          setForecastDays(Number(e.target.value))
                        }
                        SelectProps={{ native: true }}
                        sx={{ "& select": { fontSize: "12px", py: 0.5 } }}
                      >
                        <option value={7}>Next 7 Days</option>
                        <option value={14}>Next 14 Days</option>
                        <option value={30}>Next 30 Days</option>
                      </TextField>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={fetchReportData}
                        sx={{ textTransform: "none", fontSize: "12px" }}
                      >
                        Update
                      </Button>
                    </Box>
                  </Box>
                  <RevenueForecastChart data={data.revenueForecast} />
                </CardContent>
              </Card>
            </Grid>

            {/* Time of Use Distribution */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Time-of-Use Distribution
                  </Typography>
                  <TimeOfUseDonutChart data={data.touDistribution} />
                </CardContent>
              </Card>
            </Grid>

            {/* Site Cost Trend */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Site-wise Cost Trends (3D Moving Avg)
                  </Typography>
                  <SiteCostTrendChart data={data.siteCostTrend} />
                </CardContent>
              </Card>
            </Grid>

            {/* Consumption vs Cost Correlation */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Consumption vs Cost Correlation
                  </Typography>
                  <CorrelationScatterChart data={data.siteCostTrend} />
                </CardContent>
              </Card>
            </Grid>

            {/* Bill Distribution */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Bill Amount Distribution
                  </Typography>
                  <BillDistributionChart data={data.billDistribution} />
                </CardContent>
              </Card>
            </Grid>

            {/* Balance Aging */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Outstanding Balance Aging
                  </Typography>
                  <BalanceAgingChart data={data.balanceAging} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Layout>
  );
};

export default BillingAnalyticsV2;
